import { themeBreakpoints } from "@common/constants";
import {
  CTA,
  DownloadablePdfUrl,
  ProductPhase,
  ProductTemplateId,
  ProductTopic,
  TaskCategory,
  TaskTemplateId,
  WizardId,
} from "./types";
import { startCase, orderBy } from "lodash";
import CareerAdvisoryIcon from "@modules/tasks/assets/CareerAdvisory";
import DebtCashManagementIcon from "@modules/tasks/assets/DebtCashManagement";
import FinancialPlanIcon from "@modules/tasks/assets/FinancialPlan";
import InsuranceIcon from "@modules/tasks/assets/Insurance";
import InvestmentsIcon from "@modules/tasks/assets/Investments";
import OnboardingIcon from "@modules/tasks/assets/Onboarding";
import TaxPlanningIcon from "@modules/tasks/assets/TaxPlanning";
import TrustEstateIcon from "@modules/tasks/assets/TrustEstate";
import OngoingWealthManagementIcon from "@modules/tasks/assets/OngoingWealthManagement";

export enum CtaType {
  Redirect = "Redirect",
  Open = "Open",
  Exect = "Exect",
  Iframe = "Iframe",
}

export enum TaskPlaceHolder {
  USER_NAME = "[USER_NAME]",
}

export enum TaskStates {
  Added = "Added",
  Paused = "Paused",
  Complete = "Complete",
  Pending = "Pending",
  InProgress = "In Progress",
}

export enum TaskType {
  ME = "me",
  ADVISOR = "advisor",
}

export enum TaskTabSelected {
  TASKS = "tasks",
  ACHIEVEMENTS = "achievements completed",
}

export const gridMargins: Record<string, [number, number]> = {
  lg: [20, 15],
  md: [20, 15],
  sm: [20, 15],
  xs: [20, 16],
  xxs: [20, 16],
};

export const gridCols = { lg: 1, md: 1, sm: 1, xs: 1, xxs: 1 };

export const gridBreakpoints = {
  lg: themeBreakpoints.lg,
  md: themeBreakpoints.md,
  sm: themeBreakpoints.sm,
  xs: themeBreakpoints.xs,
  xxs: themeBreakpoints.xxs || 0,
};

export const phaseOptions = [
  {
    id: ProductPhase.PHASE_1,
    name: "Onboarding",
  },
  {
    id: ProductPhase.PHASE_2,
    name: "Wealth Playbook",
  },
];

export const downloadablePdfUrlOptions = orderBy(
  [
    { id: null, label: "None", value: null },
    ...Object.keys(DownloadablePdfUrl).map((key) => ({
      id: DownloadablePdfUrl[key as keyof typeof DownloadablePdfUrl] as string,
      label: startCase(key.toLowerCase() as keyof typeof DownloadablePdfUrl),
      value: DownloadablePdfUrl[key as keyof typeof DownloadablePdfUrl] as
        | DownloadablePdfUrl
        | string,
    })),
  ],
  (option) => (option.id ? option.label : 0),
  ["asc"]
);

export const estimateCompletionTimeOptions = [
  { id: 0, label: "None", value: 0 },
  { id: 5, label: "5 min", value: 5 },
  { id: 10, label: "10 min", value: 10 },
  { id: 15, label: "15 min", value: 15 },
  { id: 20, label: "20 min", value: 20 },
  { id: 30, label: "30 min", value: 30 },
  { id: 45, label: "45 min", value: 45 },
  { id: 60, label: "1 hour", value: 60 },
];

export const taskActionOptions = orderBy(
  [
    { id: null, label: "None", value: null },
    ...Object.keys(CTA).map((key) => ({
      id: CTA[key as keyof typeof CTA] as string,
      label: startCase(CTA[key as keyof typeof CTA]),
      value: CTA[key as keyof typeof CTA],
    })),
  ],
  (option) => (option.id ? option.label : 0),
  ["asc"]
);

export const topicOptions = [
  ...Object.keys(ProductTopic).map((key) => ({
    id: ProductTopic[key as keyof typeof ProductTopic] as string | null,
    label: ProductTopic[key as keyof typeof ProductTopic] as
      | ProductTopic
      | string,
    value: ProductTopic[key as keyof typeof ProductTopic] as
      | ProductTopic
      | string,
  })),
  {
    id: null,
    label: "Ongoing Wealth Management",
    value: null,
  },
];

export const TaskTemplateWizardIdMap: Partial<
  Record<TaskTemplateId, WizardId>
> = {
  [TaskTemplateId.UPDATE_DISABILITY_INSURANCE_INFORMATION]:
    WizardId.DISABILITY_INSURANCE,
  [TaskTemplateId.UPDATE_DISABILITY_INSURANCE_INFORMATION_COCLIENT]:
    WizardId.DISABILITY_INSURANCE,

  [TaskTemplateId.UPDATE_TERM_LIFE_INSURANCE_INFORMATION]:
    WizardId.TERM_LIFE_INSURANCE,
  [TaskTemplateId.UPDATE_TERM_LIFE_INSURANCE_INFORMATION_COCLIENT]:
    WizardId.TERM_LIFE_INSURANCE,

  [TaskTemplateId.RISK_TOLERANCE_QUESTIONNAIRE]: WizardId.RISK_TOLERANCE,
  [TaskTemplateId.RISK_TOLERANCE_QUESTIONNAIRE_COCLIENT]:
    WizardId.RISK_TOLERANCE,

  [TaskTemplateId.UPDATE_PERSONAL_INFORMATION]: WizardId.PROFILE,
  [TaskTemplateId.UPDATE_PERSONAL_INFORMATION_COCLIENT]: WizardId.PROFILE,
  [TaskTemplateId.MGP_QUESTIONNAIRE_WIZARD]: WizardId.MONEY_GUIDE_PRO,
};

export const autogeneratedTaskTemplateIdsByProductId = {
  // Order matters for Wealth Management. Reverse Order.
  [ProductTemplateId.WEALTH_MANAGEMENT]: [
    TaskTemplateId.UPLOAD_VOIDED_CHECK,
    TaskTemplateId.UPLOAD_INVESTMENT_STATEMENTS_PHASE_1,
    TaskTemplateId.UPDATE_PERSONAL_INFORMATION,
    TaskTemplateId.UPDATE_PERSONAL_INFORMATION_COCLIENT,
  ],
  [ProductTemplateId.WEALTH_DIAGNOSTIC]: [
    TaskTemplateId.RISK_TOLERANCE_QUESTIONNAIRE,
    TaskTemplateId.SHARE_TAX_INFORMATION_WITH_YOUR_ADVISOR,
    TaskTemplateId.MGP_QUESTIONNAIRE_COCLIENT,
    TaskTemplateId.UPDATE_AUTO_INSURANCE_INFORMATION,
    TaskTemplateId.UPDATE_DISABILITY_INSURANCE_INFORMATION,
    TaskTemplateId.UPDATE_TERM_LIFE_INSURANCE_INFORMATION,
    TaskTemplateId.UPLOAD_ESTATE_PLANNING_DOCUMENTS,
    TaskTemplateId.UPLOAD_INSURANCE_POLICIES,
    TaskTemplateId.UPLOAD_INVESTMENT_STATEMENTS,
    TaskTemplateId.UPLOAD_TAX_RETURNS,
    TaskTemplateId.UPDATE_PERSONAL_INFORMATION,
    TaskTemplateId.UPDATE_PERSONAL_INFORMATION_COCLIENT,
  ],
};

export type PlaybookDomain = ProductTopic | ProductPhase.PHASE_1;
export const PLAYBOOK_DOMAINS: PlaybookDomain[] = [
  ProductTopic.ESTATE_PLAN,
  ProductTopic.DEBT_AND_CASH,
  ProductTopic.INSURANCE_PLAN,
  ProductTopic.CAREER,
  ProductTopic.TAX_PLAN,
  ProductTopic.INVESTMENT_PLAN,
  ProductTopic.FINANCIAL_PLAN,
  ProductPhase.PHASE_1,
];

export const ICONS_BY_PLAYBOOK_DOMAIN = {
  [ProductPhase.PHASE_1]: OnboardingIcon,
  [ProductTopic.FINANCIAL_PLAN]: FinancialPlanIcon,
  [ProductTopic.INVESTMENT_PLAN]: InvestmentsIcon,
  [ProductTopic.TAX_PLAN]: TaxPlanningIcon,
  [ProductTopic.TAX_CENTER]: TaxPlanningIcon,
  [ProductTopic.CAREER]: CareerAdvisoryIcon,
  [ProductTopic.INSURANCE_PLAN]: InsuranceIcon,
  [ProductTopic.DEBT_AND_CASH]: DebtCashManagementIcon,
  [ProductTopic.ESTATE_PLAN]: TrustEstateIcon,
  [TaskCategory.ONGOING_WEALTH_MANAGEMENT]: OngoingWealthManagementIcon,
};

export const NoCacheWizardExitMessage =
  "Are you sure you want to exit? Your progress will not be saved.";

export enum TaskStatus {
  Open = "Open",
  Complete = "Complete",
  Overdue = "Overdue",
}

export const TASK_SORT_ORDER = [
  TaskCategory.PRE_ONBOARDING,
  TaskCategory.ONBOARDING,
  TaskCategory.FINANCIAL_PLAN,
  TaskCategory.INVESTMENT_PLAN,
  TaskCategory.TAX_PLAN,
  TaskCategory.CAREER,
  TaskCategory.INSURANCE_PLAN,
  TaskCategory.DEBT_AND_CASH,
  TaskCategory.ESTATE_PLAN,
  TaskCategory.ONGOING_WEALTH_MANAGEMENT,
];

export const NOTIFICATION_DISABLED_REASONS = {
  NO_TASK_NOTIFICATIONS_ENABLED: "This client has task notifications disabled",
};

export const READ_ONLY_PROFILE_WIZARD_FIELDS = [
  "EMAIL",
  "FIRST_NAME",
  "LAST_NAME",
  "PHONE_NUMBER",
  "NPI_NUMBER",
  "SPECIALTY",
  "CAREER_STAGE",
  "ADDRESS_STREET",
  "ADDRESS_STREET_2",
  "ADDRESS_CITY",
  "ADDRESS_STATE",
  "ADDRESS_ZIP",
  "MAILING_ADDRESS_STREET",
  "MAILING_ADDRESS_STREET_2",
  "MAILING_ADDRESS_CITY",
  "MAILING_ADDRESS_STATE",
  "MAILING_ADDRESS_ZIP",
] as const;
